import { apiRequest, ApiResponse as ApiResponseType } from './api';

export type MensagemEvento =
  | 'aniversario_cliente'
  | 'lembrete_primeiro_envio_antes'
  | 'lembrete_segundo_envio_antes'
  | 'lembrete_dia_vencimento'
  | 'lembrete_primeiro_envio_apos'
  | 'lembrete_segundo_envio_apos'
  | 'fatura_criada'
  | 'confirmacao_pagamento'
  | 'novo_chamado'
  | 'boas_vindas';

export interface Mensagem {
  id: number;
  status: 'enviado' | 'aguardando_envio' | 'falhou';
  tipo: 'sms' | 'whatsapp' | 'email' | 'aplicativo';
  destino: string;
  conteudo: string;
  data_envio?: string | null;
  agendado_para?: string | null;
  erro_mensagem?: string | null;
  cliente_id?: number | null;
  usuario_id?: number | null;
  gateway_id?: number | null;
  tentativas: number;
  evento?: MensagemEvento | string | null;
  fatura_id?: number | null;
  contrato_id?: number | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  cliente?: {
    id: number;
    nome: string;
    cpf_cnpj: string;
  };
  usuario?: {
    id: number;
    nome: string;
    usuario: string;
  };
  contato?: {
    id: number;
    ddi?: string | null;
    numero?: string | null;
    email?: string | null;
    tipo?: 'email' | 'fixo' | 'celular';
  };
}

export interface MensagemFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  status?: 'enviado' | 'aguardando_envio' | 'falhou';
  tipo?: 'sms' | 'whatsapp' | 'email' | 'aplicativo';
  evento?: MensagemEvento | string;
  destino?: string;
  cliente_id?: number;
  usuario_id?: number;
  data_inicio?: string;
  data_fim?: string;
  criacao_inicio?: string;
  criacao_fim?: string;
  agendamento_inicio?: string;
  agendamento_fim?: string;
  envio_inicio?: string;
  envio_fim?: string;
  search?: string;
}

export interface CreateMensagemData {
  tipo: 'sms' | 'whatsapp' | 'email' | 'aplicativo';
  destino: number;
  conteudo: string;
  cliente_id?: number;
  gateway_id?: number;
  fatura_id?: number;
  enviar_fatura_pdf?: boolean;
  sendNow?: boolean;
}

export interface UpdateStatusData {
  status: 'enviado' | 'aguardando_envio' | 'falhou';
  erro_mensagem?: string;
  data_envio?: string;
}

export interface MensagemStats {
  total: number;
  porStatus: Array<{
    status: string;
    total: number;
  }>;
  porTipo: Array<{
    tipo: string;
    total: number;
  }>;
}

export type SendNowOutcome = 'enviado' | 'cancelado' | 'reagendado' | 'falhou';

export interface SendNowMensagemResult {
  success: boolean;
  outcome: SendNowOutcome;
  message?: string;
  data?: Mensagem;
}

const MENSAGEM_CANCELADA_PREFIX = 'Mensagem cancelada';
const MENSAGEM_REAGENDADA_PREFIX = 'Mensagem reagendada';

const resolveSendNowOutcome = (response: ApiResponseType<Mensagem>): SendNowOutcome => {
  if (response.success) return 'enviado';
  const msg = response.message || '';
  if (msg.startsWith(MENSAGEM_CANCELADA_PREFIX)) return 'cancelado';
  if (msg.startsWith(MENSAGEM_REAGENDADA_PREFIX)) return 'reagendado';
  return 'falhou';
};

class MensagemService {
  // Listar mensagens com filtros
  async getMensagens(filters?: MensagemFilters): Promise<ApiResponseType<Mensagem[]>> {
    const params: any = {};

    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.sortBy) params.sortBy = filters.sortBy;
    if (filters?.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters?.status) params.status = filters.status;
    if (filters?.tipo) params.tipo = filters.tipo;
    if (filters?.evento) params.evento = filters.evento;
    if (filters?.destino) params.destino = filters.destino;
    if (filters?.cliente_id) params.cliente_id = filters.cliente_id;
    if (filters?.usuario_id) params.usuario_id = filters.usuario_id;
    if (filters?.data_inicio) params.data_inicio = filters.data_inicio;
    if (filters?.data_fim) params.data_fim = filters.data_fim;
    if (filters?.criacao_inicio) params.criacao_inicio = filters.criacao_inicio;
    if (filters?.criacao_fim) params.criacao_fim = filters.criacao_fim;
    if (filters?.agendamento_inicio) params.agendamento_inicio = filters.agendamento_inicio;
    if (filters?.agendamento_fim) params.agendamento_fim = filters.agendamento_fim;
    if (filters?.envio_inicio) params.envio_inicio = filters.envio_inicio;
    if (filters?.envio_fim) params.envio_fim = filters.envio_fim;
    if (filters?.search) params.search = filters.search;

    return await apiRequest.get<Mensagem[]>('/mensagens', { params });
  }

  // Buscar mensagem por ID
  async getMensagemById(id: number): Promise<ApiResponseType<Mensagem>> {
    return await apiRequest.get<Mensagem>(`/mensagens/${id}`);
  }

  // Criar nova mensagem
  async createMensagem(data: CreateMensagemData): Promise<ApiResponseType<Mensagem>> {
    return await apiRequest.post<Mensagem>('/mensagens', data);
  }

  // Atualizar status da mensagem
  async updateStatus(id: number, data: UpdateStatusData): Promise<ApiResponseType<Mensagem>> {
    return await apiRequest.patch<Mensagem>(`/mensagens/${id}/status`, data);
  }

  // Reenviar mensagem que falhou
  async resendMensagem(id: number): Promise<ApiResponseType<Mensagem>> {
    return await apiRequest.post<Mensagem>(`/mensagens/${id}/resend`);
  }

   // Enviar mensagem imediatamente
   async sendNowMensagem(id: number): Promise<SendNowMensagemResult> {
    const response = await apiRequest.post<Mensagem>(`/mensagens/${id}/sendNow`);
    return {
      success: response.success,
      outcome: resolveSendNowOutcome(response),
      message: response.message,
      data: response.data,
    };
  }

  // Deletar mensagem
  async deleteMensagem(id: number): Promise<ApiResponseType> {
    return await apiRequest.delete(`/mensagens/${id}`);
  }

  // Obter estatísticas de mensagens
  async getStats(data_inicio?: string, data_fim?: string): Promise<ApiResponseType<MensagemStats>> {
    const params: any = {};
    if (data_inicio) params.data_inicio = data_inicio;
    if (data_fim) params.data_fim = data_fim;

    return await apiRequest.get<MensagemStats>('/mensagens/stats', { params });
  }
}

export default new MensagemService();

